import { Phone, MapPin, Bike, Gift, Mail, Clock, ExternalLink, Navigation } from "lucide-react";
import { BRAND, HOURS, MAPS_EMBED, MAPS_DIRECTIONS } from "../data/restaurant";
import { Reveal } from "./motion/Reveal";
import { BrushUnderline } from "./BrushHeading";

const CARDS = [
  {
    icon: Phone,
    title: "Call for Takeout",
    body: BRAND.phoneDisplay,
    href: BRAND.phoneHref,
    testid: "phone-cta-link",
  },
  {
    icon: Mail,
    title: "Email Us",
    body: BRAND.email,
    href: `mailto:${BRAND.email}`,
    testid: "email-cta-link",
  },
  {
    icon: MapPin,
    title: "Location",
    body: BRAND.address,
    testid: "store-address-info",
  },
  {
    icon: Bike,
    title: "Delivery Partners",
    body: "UberEATS & DoorDash",
    testid: "delivery-partners-info",
  },
  {
    icon: Gift,
    title: "Deals & Discounts",
    body: "For in-person pickup orders",
    testid: "deals-discounts-info",
  },
];

export default function ContactBand() {
  return (
    <section id="contact" data-testid="contact-section" className="grain relative overflow-hidden border-y-4 border-jet bg-chestnut py-20 sm:py-28">
      <div aria-hidden="true" className="pointer-events-none absolute -left-20 top-10 h-72 w-72 rounded-full bg-ochre/15 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal className="text-center">
          <h2 className="font-display text-3xl font-black uppercase leading-[1.05] tracking-tight text-cream sm:text-5xl">
            Find us. <BrushUnderline>Feed your soul.</BrushUnderline>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base font-medium text-cream/80">
            Takeout, delivery, or a quick pickup deal — we are one call away.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {CARDS.map((card, i) => {
            const Icon = card.icon;
            const inner = (
              <>
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border-[3px] border-jet bg-amber text-jet shadow-hard-sm transition-transform duration-200 group-hover:-rotate-6 group-hover:scale-105">
                  <Icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-display text-lg font-black uppercase text-cream">{card.title}</h3>
                <p className="mt-1.5 flex items-start gap-1.5 text-sm font-bold leading-snug text-sunshine sm:text-base">
                  {card.body}
                  {card.href && <ExternalLink className="mt-0.5 h-4 w-4 shrink-0 opacity-60" aria-hidden="true" />}
                </p>
              </>
            );
            return (
              <Reveal key={card.title} delay={i * 0.06} className="h-full">
                {card.href ? (
                  <a
                    href={card.href}
                    data-testid={card.testid}
                    className="group block h-full rounded-3xl border-[3px] border-cream/20 bg-cream/5 p-6 transition-all duration-200 hover:-translate-y-1.5 hover:border-cream/50 hover:bg-cream/10"
                  >
                    {inner}
                  </a>
                ) : (
                  <div data-testid={card.testid} className="group h-full rounded-3xl border-[3px] border-cream/20 bg-cream/5 p-6">{inner}</div>
                )}
              </Reveal>
            );
          })}

          <Reveal delay={0.3} className="h-full">
            <div className="group h-full rounded-3xl border-[3px] border-jet bg-amber p-6 shadow-hard" data-testid="open-hours-panel">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border-[3px] border-jet bg-cream text-jet shadow-hard-sm">
                <Clock className="h-6 w-6" aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-display text-lg font-black uppercase text-jet">Open Hours</h3>
              <ul className="mt-2 space-y-1">
                {HOURS.map((h) => (
                  <li key={h.day} data-testid="hours-row" className="flex items-baseline justify-between gap-2 text-sm font-bold text-jet">
                    <span>{h.day}</span>
                    <span className="dots-leader !mb-0 !translate-y-0 opacity-40" aria-hidden="true" />
                    <span className="font-display">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-6">
          <div className="overflow-hidden rounded-3xl border-[3px] border-cream/25 bg-cream/5" data-testid="map-panel">
            <iframe
              title="Pattista location on Google Maps — 120 Lowson Cres #2, Winnipeg"
              src={MAPS_EMBED}
              className="h-[320px] w-full border-0 sm:h-[380px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <div className="flex flex-col items-center justify-between gap-3 border-t-[3px] border-cream/25 px-5 py-4 sm:flex-row">
              <p className="flex items-center gap-2 text-sm font-bold text-cream" data-testid="map-address-label">
                <MapPin className="h-4 w-4 shrink-0 text-sunshine" aria-hidden="true" />
                {BRAND.address}
              </p>
              <a
                href={MAPS_DIRECTIONS}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="get-directions-button"
                className="inline-flex items-center gap-2 rounded-full border-[3px] border-jet bg-amber px-5 py-2.5 font-display text-sm font-extrabold uppercase text-jet shadow-hard-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-hard"
              >
                <Navigation className="h-4 w-4" aria-hidden="true" />
                Get Directions
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
